import { Controller } from "@hotwired/stimulus"

// Hover an empty part of a day column in the week or day grid to preview the appointment
// that would be created there, and click to open the drawer already filled in with it.
//
// The preview is what makes the affordance discoverable: an empty time grid gives no hint
// that it is clickable, and a click that silently guesses a time is worse than no click.
const SNAP_MINUTES = 15
const DURATION_MINUTES = 60
const MINUTES_PER_DAY = 1440

export default class extends Controller {
  static targets = ["column"]
  static values = { url: String, label: String }

  connect() {
    this.ghost = null
  }

  disconnect() {
    this.hide()
  }

  // data-action="pointermove->calendar-slot#preview"
  preview(event) {
    // Touch has no hover, and a drag or resize in progress owns the pointer.
    if (event.pointerType !== "mouse" || event.buttons > 0) return this.hide()

    const column = this.columnFor(event)
    if (!column) return this.hide()

    const minutes = this.minutesAt(column, event.clientY)
    const ghost = this.ghostElement()
    ghost.style.top = `${(minutes / MINUTES_PER_DAY) * 100}%`
    ghost.style.height = `${(DURATION_MINUTES / MINUTES_PER_DAY) * 100}%`
    ghost.textContent = `${this.labelValue} · ${this.clock(minutes)} – ${this.clock(minutes + DURATION_MINUTES)}`
    if (ghost.parentElement !== column) column.appendChild(ghost)
  }

  // data-action="pointerleave->calendar-slot#hide"
  hide() {
    if (this.ghost) this.ghost.remove()
  }

  // data-action="click->calendar-slot#create"
  async create(event) {
    const column = this.columnFor(event)
    if (!column) return

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("start_date", column.dataset.date)
    url.searchParams.set("start_minutes", this.minutesAt(column, event.clientY))
    this.hide()

    // Asking for the stream directly rather than navigating: the drawer is appended to the
    // document, so a frame visit from inside calendar_grid would have nowhere to land.
    try {
      const response = await fetch(url.toString(), {
        credentials: "same-origin",
        headers: { Accept: "text/vnd.turbo-stream.html" },
      })
      const contentType = response.headers.get("Content-Type") || ""
      const html = await response.text()
      if (contentType.includes("text/vnd.turbo-stream.html")) window.Turbo.renderStreamMessage(html)
    } catch (error) {
      console.error("Error opening the new appointment drawer:", error)
    }
  }

  // Only empty space counts: over an existing block the click belongs to that appointment.
  columnFor(event) {
    if (event.target.closest("a, button, [data-calendar-drag-target='chip']")) return null

    return event.target.closest("[data-drop-precision='time']")
  }

  minutesAt(column, clientY) {
    const rect = column.getBoundingClientRect()
    if (!rect.height) return 0

    const raw = ((clientY - rect.top) / rect.height) * MINUTES_PER_DAY
    const snapped = Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES
    return Math.min(Math.max(snapped, 0), MINUTES_PER_DAY - DURATION_MINUTES)
  }

  ghostElement() {
    if (this.ghost) return this.ghost

    const ghost = document.createElement("div")
    ghost.className =
      "pointer-events-none absolute inset-x-1 z-0 flex items-start rounded-md border border-dashed " +
      "border-green-500 bg-green-50/70 px-1.5 py-0.5 text-xs font-medium text-green-800"
    this.ghost = ghost
    return ghost
  }

  clock(minutes) {
    const wrapped = minutes % MINUTES_PER_DAY
    const hours = String(Math.floor(wrapped / 60)).padStart(2, "0")
    return `${hours}:${String(wrapped % 60).padStart(2, "0")}`
  }
}
